import { useState } from "react";
import { Play } from "lucide-react";
import { getToolVideo, videoDisplayTitle, type ToolVideo } from "@/lib/tool-videos";
import { trackEvent } from "@/lib/analytics";

interface Props {
  toolId: string;
  toolName?: string;
  /** A video that is not tied to a tool (for example a section intro). Overrides the lookup by toolId. */
  video?: ToolVideo;
  heading?: string;
  /** Heading level for the label; use "h3" when the embed sits inside a card that already has an h2. */
  headingAs?: "h2" | "h3";
  /** Analytics id for the play event when the video is not tied to a tool (defaults to toolId). */
  trackId?: string;
}

/**
 * Compact video block. Shows the YouTube thumbnail with a play button and
 * only loads the player when tapped: no empty box, no third-party player
 * script on page load. It is still the official YouTube player, so plays
 * count as views on our channel.
 */
export function ToolVideoEmbed({ toolId, toolName, video: given, heading, headingAs = "h2", trackId }: Props) {
  const Heading = headingAs;
  const video = given ?? getToolVideo(toolId);
  const [playing, setPlaying] = useState(false);
  if (!video) return null;

  const watchUrl = `https://www.youtube.com/watch?v=${video.videoId}`;
  const embedUrl = `https://www.youtube.com/embed/${video.videoId}?rel=0&autoplay=1`;
  const poster = `https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`;
  // Different from the "How to use" wording elsewhere on the page so the
  // headings stay unique.
  const label = heading ?? (toolName ? `Watch: ${toolName} in Action` : videoDisplayTitle(video.title));
  const shownTitle = videoDisplayTitle(video.title);

  return (
    <div className="min-w-0">
      <Heading className="text-base font-semibold mb-2">{label}</Heading>
      <div className="relative w-full overflow-hidden rounded-xl border border-border/60 bg-muted/30 aspect-video shadow-sm">
        {playing ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={embedUrl}
            title={shownTitle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setPlaying(true);
              trackEvent("tool_video_play", { tool_id: trackId ?? toolId });
            }}
            className="group absolute inset-0 h-full w-full"
            aria-label={`Play video: ${shownTitle}`}
          >
            <img src={poster} alt="" width={480} height={360} loading="lazy" className="h-full w-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                <Play className="h-6 w-6 ml-0.5" aria-hidden />
              </span>
            </span>
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="inline-block py-3 -my-2 text-primary hover:underline underline-offset-2">
          Watch on YouTube
        </a>{" "}
        ·{" "}
        <a
          href="https://www.youtube.com/@freedailypro?sub_confirmation=1"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block py-3 -my-2 text-primary hover:underline underline-offset-2"
        >
          Subscribe for new tutorials
        </a>
      </p>
    </div>
  );
}
